import {
  forwardRef,
  useCallback,
  useId,
  useRef,
  type ComponentPropsWithoutRef
} from 'react'

import { LayoutGroup, motion } from 'motion/react'
import { tv } from 'tailwind-variants'

import { useReducedMotion } from '../../../hooks/use-reduced-motion'
import { cn } from '../../../utils/cn'
import { Button } from '../../atoms/button/button'
import { Icon } from '../../atoms/icon/icon'

import { PaginationJump } from './pagination-jump'

export interface PaginationProps
  extends Omit<ComponentPropsWithoutRef<'nav'>, 'children'> {
  /** One-based page number. Values outside the available range are clamped. */
  currentPage: number
  /** A non-negative integer. Zero pages render no navigation. */
  totalPages: number
  /**
   * `method` says which control was used: first and last count as previous and
   * next, and the ellipsis page field as jump.
   */
  onPageChange: (
    page: number,
    method: 'previous' | 'next' | 'number' | 'jump'
  ) => void
  disabled?: boolean
  /** Slides the active page indicator. Reduced-motion preferences take precedence. */
  animated?: boolean
}

type PageItem = number | 'start-ellipsis' | 'end-ellipsis'

const paginationVariants = tv(
  {
    slots: {
      root: 'font-body',
      list: 'm-0 flex list-none flex-wrap items-center gap-1 p-0',
      item: 'flex shrink-0',
      page: 'text-control-lg box-border h-8 min-w-8 px-1 py-0',
      arrow:
        'data-[disabled]:not-data-[loading]:border-transparent data-[disabled]:not-data-[loading]:text-content-disabled size-6 p-0 [&_[data-slot=icon]]:size-6 [&_[data-slot=icon]]:text-[24px]/none',
      ellipsis: 'text-control-lg box-border size-8 px-0 py-0'
    },
    variants: {
      selected: { true: { page: 'bg-primary-400' } }
    }
  },
  { twMerge: false }
)

const styles = paginationVariants()

/** Five slots keep the control compact, with both boundaries always reachable. */
export const getPageItems = (
  currentPage: number,
  totalPages: number
): PageItem[] => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1)
  }
  if (currentPage <= 3) return [1, 2, 3, 'end-ellipsis', totalPages]
  if (currentPage >= totalPages - 2) {
    return [1, 'start-ellipsis', totalPages - 2, totalPages - 1, totalPages]
  }
  return [1, 'start-ellipsis', currentPage, 'end-ellipsis', totalPages]
}

interface PaginationArrowProps {
  label: string
  icon: string
  page: number
  method: 'previous' | 'next'
  disabled: boolean
  onPageChange: PaginationProps['onPageChange']
}

const PaginationArrow = ({
  label,
  icon,
  page,
  method,
  disabled,
  onPageChange
}: PaginationArrowProps) => (
  <li className={styles.item()}>
    <Button
      variant='tertiary'
      size='sm'
      iconOnly
      className={styles.arrow()}
      aria-label={label}
      disabled={disabled}
      onClick={() => onPageChange(page, method)}
    >
      <Icon name={icon} aria-hidden='true' />
    </Button>
  </li>
)

export const Pagination = forwardRef<HTMLElement, PaginationProps>(
  (
    {
      currentPage,
      totalPages,
      onPageChange,
      disabled = false,
      animated = false,
      className,
      'aria-label': ariaLabel = 'Pagination',
      ...props
    },
    ref
  ) => {
    const containerRef = useRef<HTMLElement | null>(null)
    const layoutId = useId()
    const reducedMotion = useReducedMotion()
    const shouldAnimate = animated && !reducedMotion && !disabled
    const handleContainerRef = useCallback(
      (node: HTMLElement | null) => {
        containerRef.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) ref.current = node
      },
      [ref]
    )

    if (!Number.isSafeInteger(totalPages) || totalPages < 1) return null

    const page = Number.isFinite(currentPage)
      ? Math.min(totalPages, Math.max(1, Math.floor(currentPage)))
      : 1
    const isFirstPage = page === 1
    const isLastPage = page === totalPages

    return (
      <nav
        ref={handleContainerRef}
        data-slot='pagination'
        tabIndex={-1}
        aria-label={ariaLabel}
        className={cn(styles.root(), className)}
        {...props}
      >
        <LayoutGroup id={layoutId}>
          <ul className={styles.list()}>
            <PaginationArrow
              label='First page'
              method='previous'
              icon='keyboard_double_arrow_left'
              page={1}
              disabled={disabled || isFirstPage}
              onPageChange={onPageChange}
            />
            <PaginationArrow
              label='Previous page'
              method='previous'
              icon='keyboard_arrow_left'
              page={page - 1}
              disabled={disabled || isFirstPage}
              onPageChange={onPageChange}
            />
            {getPageItems(page, totalPages).map(item => (
              <li key={item} className={styles.item()}>
                {typeof item === 'number' ? (
                  <Button
                    variant={item === page ? 'primary' : 'secondary'}
                    size='sm'
                    className={styles.page({ selected: item === page })}
                    style={
                      shouldAnimate && item === page
                        ? { backgroundColor: 'transparent' }
                        : undefined
                    }
                    contentClassName={shouldAnimate ? 'static' : undefined}
                    aria-label={`Page ${item}`}
                    aria-current={item === page ? 'page' : undefined}
                    disabled={disabled}
                    onClick={() => {
                      if (item !== page) onPageChange(item, 'number')
                    }}
                  >
                    {shouldAnimate && item === page && (
                      <motion.span
                        data-slot='pagination-indicator'
                        aria-hidden='true'
                        layoutId='active-page'
                        layoutDependency={`${page}:${totalPages}`}
                        className='bg-primary-400 pointer-events-none absolute -inset-px'
                        style={{ borderRadius: 'var(--radius-button-sm)' }}
                        initial={false}
                        transition={{
                          type: 'spring',
                          stiffness: 500,
                          damping: 40
                        }}
                      />
                    )}
                    <span className={shouldAnimate ? 'relative' : undefined}>
                      {item}
                    </span>
                  </Button>
                ) : (
                  <PaginationJump
                    key={`${page}-${totalPages}-${disabled}`}
                    containerRef={containerRef}
                    label={
                      item === 'start-ellipsis'
                        ? 'Go to an earlier page'
                        : 'Go to a later page'
                    }
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={next => onPageChange(next, 'jump')}
                    disabled={disabled}
                    className={styles.ellipsis()}
                  />
                )}
              </li>
            ))}
            <PaginationArrow
              label='Next page'
              method='next'
              icon='keyboard_arrow_right'
              page={page + 1}
              disabled={disabled || isLastPage}
              onPageChange={onPageChange}
            />
            <PaginationArrow
              label='Last page'
              method='next'
              icon='keyboard_double_arrow_right'
              page={totalPages}
              disabled={disabled || isLastPage}
              onPageChange={onPageChange}
            />
          </ul>
        </LayoutGroup>
      </nav>
    )
  }
)
Pagination.displayName = 'Pagination'
